import React, { useState, useRef, useEffect } from 'react';
import { generationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {shirtSizeVariant} from './constant';
import OutOfStockModal from './OutOfStockModal';

const GenerateStep = ({ photos, selectedDesign, selectedBackground, personalization, onBack, onReset }) => {
  const { refreshUser } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [showOutOfStockModal, setShowOutOfStockModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [watermarkedImageUrl, setWatermarkedImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [pollingStatus, setPollingStatus] = useState('');
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [sizeQuantities, setSizeQuantities] = useState({});
  const [shopUrl, setShopUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('video'); // 'image' or 'video'
  const currentPollingTaskIdRef = useRef(null);

  useEffect(() => {
    return () => { currentPollingTaskIdRef.current = null; };
  }, []);

  const handleCancel = () => {
    currentPollingTaskIdRef.current = null;
    setGenerating(false);
    setPollingStatus('');
    setError('Video generation was cancelled by user');
  };

  const handleGenerate = async () => {
    
    if (!photos || photos.length === 0) {
      setError('No photos uploaded');
      return;
    }
    if (!selectedDesign) {
      setError('No shirt design selected');
      return;
    }
    if (!selectedBackground) {
      setError('No background scene selected');
      return;
    }

    setGenerating(true);
    setError(null);
    currentPollingTaskIdRef.current = null;
    
    try {
      // Get the first uploaded photo UUID
      const uploadedPhoto = photos.find(photo => photo.uploadUUID);
      if (!uploadedPhoto) {
        throw new Error('No uploaded photo found');
      }

      // For now, we'll use placeholder UUIDs for shirt and scene images
      // In a real app, these would come from the selectedDesign and personalization
    
      // Debug the API payload
      const apiPayload = {
        uploaded_image_uuid: uploadedPhoto.uploadUUID,
        shirt_product_uuid: selectedDesign.uuid || selectedDesign.id || 'placeholder-shirt-uuid',
        scene_image_uuid: '',
        logo_image_uuid: personalization?.logoUUID || '',
        background_theme: selectedBackground?.id || '',
        custom_background: selectedBackground?.customDescription || '',
        personal_names: personalization?.names || '',
        custom_text: personalization?.customText || '',
        shirt_color: personalization?.color || '',
        size: '4K'
      };
      
      const response = await generationAPI.generate(
        uploadedPhoto.uploadUUID,
        selectedDesign.uuid || selectedDesign.id || 'placeholder-shirt-uuid',
        '', // Scene image is optional, pass empty string for now
        '4K',
        selectedBackground?.id || '',
        selectedBackground?.customDescription || '',
        personalization
      );
      

      // The response should contain the video URL or video_task_id
      
      if (response.data.video_url) {
        // Refresh user data to show updated credits
        refreshUser().catch(console.error);
        setVideoUrl(response.data.video_url);
        if (response.data.watermarked_image_url) {
          setWatermarkedImageUrl(response.data.watermarked_image_url);
        }
        setGenerating(false);
      } else if (response.data.video_task_id) {
        // Refresh user data to show updated credits
        refreshUser().catch(console.error);
        // Store the watermarked image URL if available
        if (response.data.watermarked_image_url) {
          setWatermarkedImageUrl(response.data.watermarked_image_url);
        }
    // If we get a video_task_id, we should poll for the video status
    // Set ref (synchronous) so polling sees it immediately
    currentPollingTaskIdRef.current = response.data.video_task_id;
    setPollingStatus('Starting video generation pipeline...');
        pollVideoStatus(response.data.video_task_id); // Don't await - let it run async
        return; // Exit early - polling will handle the final state changes
      } else {
        throw new Error('No video URL or task ID received from server');
      }
    } catch (err) {
      console.error('Error generating video:', err);
      setError(err.response?.data?.error || err.message || 'Failed to generate video');
      setGenerating(false);
      setPollingAttempts(0);
    }
  };

  const pollVideoStatus = (taskId) => {
    const maxAttempts = 60; // Poll for up to 10 minutes (60 * 10 seconds)
    let attempts = 0;

    const poll = async () => {

      // Stop if cancelled
      if (currentPollingTaskIdRef.current !== taskId) {
        return;
      }

      try {
        setPollingAttempts(attempts + 1);
        const response = await generationAPI.getVideoStatus(taskId);
        const { status, s3_video_url, error_message, video_url, person_count, size_detection_data, shop_url } = response.data;

        if (status === 'completed' && (s3_video_url || video_url)) {
          if (currentPollingTaskIdRef.current === taskId) {
            setVideoUrl(s3_video_url || video_url);
            setPollingStatus('');
            setPollingAttempts(0);
            setGenerating(false);
            
            // Store shop URL from API response
            if (shop_url) {
              setShopUrl(shop_url);
            }
            
            // Extract person count and size quantities
            if (person_count !== undefined && person_count !== null) {
              // Set default size quantity if not provided
              setSizeQuantities({ M: person_count });
            }
            
            if (size_detection_data) {
              try {
                const sizeData = typeof size_detection_data === 'string' 
                  ? JSON.parse(size_detection_data) 
                  : size_detection_data;
                
                if (sizeData && sizeData.size_quantities) {
                  setSizeQuantities(sizeData.size_quantities);
                }
              } catch (e) {
                console.error('Failed to parse size detection data:', e);
              }
            }
            
            currentPollingTaskIdRef.current = null;
          }
          return;
        }

        if (status === 'failed' || error_message) {
          throw new Error(error_message || 'Video generation failed');
        }

        if (status === 'pending' || status === 'processing') {
          if (currentPollingTaskIdRef.current === taskId) {
            setPollingStatus(status === 'pending' ? '🔄 Queued for processing...' : '🎬 Generating your AI video...');
          }
          attempts++;
          if (attempts < maxAttempts && currentPollingTaskIdRef.current === taskId) {
            setTimeout(poll, 10000);
          } else if (currentPollingTaskIdRef.current === taskId) {
            setError('Video generation timed out after 10 minutes');
            setGenerating(false);
            setPollingStatus('');
            setPollingAttempts(0);
            currentPollingTaskIdRef.current = null;
            return;
          }
          return;
        }

        console.warn(`Unknown status: ${status}`);
        if (currentPollingTaskIdRef.current === taskId) {
          setPollingStatus(`🔧 Processing (${status})...`);
        }
        attempts++;
        if (attempts < maxAttempts && currentPollingTaskIdRef.current === taskId) {
          setTimeout(poll, 10000);
        } else if (currentPollingTaskIdRef.current === taskId) {
          setError(`Video generation timed out. Last status: ${status}`);
          setGenerating(false);
          setPollingStatus('');
          setPollingAttempts(0);
          currentPollingTaskIdRef.current = null;
          return;
        }
      } catch (err) {
        if (currentPollingTaskIdRef.current === taskId) {
          console.error('Error polling video status:', err);
          setError(err.response?.data?.error || err.message || 'Failed to get video status');
          setGenerating(false);
          setPollingStatus('');
          setPollingAttempts(0);
          currentPollingTaskIdRef.current = null;
        }
      }
    };

    // Start polling immediately
    poll();
  };

  const generateCaption = () => {
    const designName = selectedDesign?.name || 'Custom';
    const themeName = selectedBackground?.name || 'Original';
    
    return `Check out my AI-generated fashion video! 🎬✨\n\n` +
           `Design: ${designName}\n` +
           `Theme: ${themeName}\n\n` +
           `Create your own at Pakainow.AI!\n\n` +
           `#PakaiNowAI #MyAITee #WearTheFuture #AIFashion #CustomTShirt #FashionTech`;
  };

  const handleDownloadImage = async () => {
    if (!watermarkedImageUrl) return;
    
    try {
      const response = await fetch(watermarkedImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `pakainow-image-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback to opening in new tab
      window.open(watermarkedImageUrl, '_blank');
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `pakainow-video-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading video:', error);
      // Fallback to opening in new tab
      window.open(videoUrl, '_blank');
    }
  };

  const handleShareImage = (platform) => {
    const caption = generateCaption();
    // Use backend share URL for proper Open Graph meta tags
    const taskId = currentPollingTaskIdRef.current;
    const shareUrl = taskId 
      ? `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/share/video/${taskId}`
      : watermarkedImageUrl;
    const directImageUrl = watermarkedImageUrl;
    
    if (platform === 'copy') {
      const shareText = `${caption}\n\n${shareUrl}`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Shareable URL and caption copied to clipboard!');
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Shareable URL and caption copied to clipboard!');
      });
    } else if (platform === 'whatsapp') {
      const text = encodeURIComponent(`${caption}\n\n${shareUrl}`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
    } else if (platform === 'facebook') {
      // Use the backend share URL for proper Open Graph tags with caption
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`, '_blank');
    } else if (platform === 'twitter') {
      const text = encodeURIComponent(caption);
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'instagram') {
      // Instagram doesn't support direct URL sharing, copy to clipboard with instructions
      const shareText = `${caption}\n\nImage: ${directImageUrl}\n\n📱 Download the image and share it on Instagram!`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Caption copied! Download the image and paste the caption when sharing on Instagram.');
      });
    }
  };

  const handleShare = (platform) => {
    const caption = generateCaption();
    // Use backend share URL for proper Open Graph meta tags
    const taskId = currentPollingTaskIdRef.current;
    const shareUrl = taskId 
      ? `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/share/video/${taskId}`
      : videoUrl;
    const directVideoUrl = videoUrl;
    
    if (platform === 'copy') {
      const shareText = `${caption}\n\n${shareUrl}`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Shareable URL and caption copied to clipboard!');
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Shareable URL and caption copied to clipboard!');
      });
    } else if (platform === 'whatsapp') {
      const text = encodeURIComponent(`${caption}\n\n${shareUrl}`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
    } else if (platform === 'facebook') {
      // Use the backend share URL for proper Open Graph tags with caption
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`, '_blank');
    } else if (platform === 'twitter') {
      const text = encodeURIComponent(caption);
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'tiktok') {
      // TikTok doesn't support direct URL sharing, copy to clipboard with instructions
      const shareText = `${caption}\n\nVideo: ${directVideoUrl}\n\n📱 Download the video and share it on TikTok!`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Caption copied! Download the video and paste the caption when sharing on TikTok.');
      });
    } else if (platform === 'instagram') {
      // Instagram doesn't support direct URL sharing, copy to clipboard with instructions
      const shareText = `${caption}\n\nVideo: ${directVideoUrl}\n\n📱 Download the video and share it on Instagram!`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Caption copied! Download the video and paste the caption when sharing on Instagram.');
      });
    }
  };

  return (
    <div className="upload-section">
      <div className="step-header-with-nav">
        <div className="navigation-buttons">
          <button className="btn-back" onClick={onBack}>
            ← Back
          </button>
        </div>
        <h2 style={{ fontSize: '24px', margin: '0' }}>
          Generate Your AI Fashion Video
        </h2>
        <div className="navigation-buttons">
          <button 
            className="btn-next" 
            onClick={handleGenerate} 
            disabled={generating}
            style={{
              opacity: generating ? 0.5 : 1,
              cursor: generating ? 'not-allowed' : 'pointer'
            }}
          >
            {generating ? 'Generating...' : 'Generate Video'}
          </button>
        </div>
      </div>
      <p style={{ textAlign: 'center', color: '#718096', marginBottom: '30px' }}>
        Review your selections and create your viral AI video
      </p>

      <div className="generate-section">
        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #f87171',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            color: '#dc2626'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {!generating && !videoUrl && (
          <>
            <div className="generate-preview">
              <h3 style={{ marginBottom: '20px', color: '#1a202c' }}>Preview</h3>
              
              <div style={{ marginBottom: '20px' }}>
                <strong>Photos:</strong> {photos.length} uploaded
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <strong>Design:</strong> {selectedDesign?.name || 'None selected'}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <strong>Background:</strong> {selectedBackground?.name || 'None selected'}
                {selectedBackground?.customDescription && (
                  <div style={{ fontSize: '14px', color: '#718096', marginTop: '4px' }}>
                    "{selectedBackground.customDescription}"
                  </div>
                )}
              </div>
              
              {personalization.names && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>Names:</strong> {personalization.names}
                </div>
              )}
              
              {personalization.customText && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>Custom Text:</strong> {personalization.customText}
                </div>
              )}
              
              
              {personalization.logo && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Logo:</strong>
                  <div style={{ 
                    marginTop: '8px',
                    display: 'inline-block',
                    maxWidth: '150px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px',
                    background: 'white'
                  }}>
                    <img 
                      src={personalization.logo}
                      alt="Logo preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        borderRadius: '4px'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>



            <p style={{ marginTop: '15px', fontSize: '13px', color: '#718096' }}>
              This will use 1 credit • Estimated time: ~6 minutes
            </p>
          </>
        )}

        {generating && (
          <div className="loading">
            <div className="spinner"></div>
            <h3>
              {pollingStatus ? 'Processing your AI fashion video...' : 'Creating your AI fashion video...'}
            </h3>
            {/* {pollingStatus && (
              <p style={{ color: '#4F46E5', marginTop: '10px', fontWeight: 'bold' }}>
                {pollingStatus} {pollingAttempts > 0 && `(${pollingAttempts}/60)`}
              </p>
            )} */}
            <p style={{ color: '#718096' }}>
              {pollingStatus ? 
                'Your video is being generated. This typically takes 1-3 minutes.' : 
                'This may take a moment. Please don\'t close this page.'
              }
            </p>

            {/* Show watermarked image preview while generating */}
            {watermarkedImageUrl && (
              <div style={{
                marginTop: '30px',
                padding: '20px',
                background: '#f7fafc',
                borderRadius: '12px',
                maxWidth: '600px',
                margin: '30px auto 0'
              }}>
                <h4 style={{ marginBottom: '15px', color: '#1a202c', textAlign: 'center' }}>
                  📸 Preview Image
                </h4>
                <img
                  src={watermarkedImageUrl}
                  alt="Watermarked preview"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <p style={{ 
                  marginTop: '10px', 
                  fontSize: '14px', 
                  color: '#718096',
                  textAlign: 'center'
                }}>
                  Your video is being generated from this image
                </p>

                {/* Share image buttons */}
                <div style={{ marginTop: '20px' }}>
                  <h5 style={{ 
                    marginBottom: '10px', 
                    color: '#1a202c', 
                    textAlign: 'center',
                    fontSize: '14px'
                  }}>
                    Share Image Now
                  </h5>
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={handleDownloadImage}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#4F46E5',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      📥 Download
                    </button>
                    <button
                      onClick={() => handleShareImage('whatsapp')}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#25D366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      WhatsApp
                    </button>
                    <button
                      onClick={() => handleShareImage('facebook')}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#1877F2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Facebook
                    </button>
                    <button
                      onClick={() => handleShareImage('twitter')}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#1DA1F2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      X
                    </button>
                    <button
                      onClick={() => handleShareImage('instagram')}
                      style={{
                        padding: '8px 12px',
                        background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Instagram
                    </button>
                    <button
                      onClick={() => handleShareImage('copy')}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#6B7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* {pollingAttempts > 0 && (
              <div style={{ 
                marginTop: '15px', 
                padding: '10px', 
                backgroundColor: '#F3F4F6', 
                borderRadius: '8px',
                fontSize: '14px',
                color: '#6B7280'
              }}>
                <div style={{ marginBottom: '5px' }}>
                  Progress: {Math.min(pollingAttempts * 2, 100)}%
                </div>
                <div style={{
                  width: '100%',
                  height: '6px',
                  backgroundColor: '#E5E7EB',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min(pollingAttempts * 2, 100)}%`,
                    height: '100%',
                    backgroundColor: '#4F46E5',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ marginTop: '5px', fontSize: '12px' }}>
                  Elapsed: {Math.floor(pollingAttempts * 10 / 60)}:{String(pollingAttempts * 10 % 60).padStart(2, '0')} 
                  {pollingAttempts < 30 && ' • Est. remaining: 1-2 minutes'}
                </div>
              </div>
            )}
            {pollingStatus && pollingAttempts > 0 && (
              <button
                onClick={handleCancel}
                style={{
                  marginTop: '20px',
                  padding: '10px 20px',
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel Generation
              </button>
            )} */}
          </div>
        )}

        {videoUrl && !generating && (
          <div>
            <div style={{
              background: '#f7fafc',
              padding: '40px',
              borderRadius: '12px',
              marginBottom: '30px'
            }}>
              <h3 style={{ marginBottom: '20px', color: '#1a202c' }}>
                🎉 Your Video is Ready!
              </h3>
              
              {/* Tab Navigation */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '30px',
                gap: '10px',
                borderBottom: '2px solid #e2e8f0'
              }}>
                <button
                  onClick={() => setActiveTab('image')}
                  style={{
                    padding: '12px 32px',
                    background: activeTab === 'image' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: activeTab === 'image' ? 'white' : '#4a5568',
                    border: 'none',
                    borderBottom: activeTab === 'image' ? '3px solid transparent' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.3s',
                    marginBottom: '-2px'
                  }}
                  onMouseOver={(e) => {
                    if (activeTab !== 'image') {
                      e.target.style.background = '#f7fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeTab !== 'image') {
                      e.target.style.background = 'transparent';
                    }
                  }}
                >
                  🖼️ Image
                </button>
                <button
                  onClick={() => setActiveTab('video')}
                  style={{
                    padding: '12px 32px',
                    background: activeTab === 'video' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: activeTab === 'video' ? 'white' : '#4a5568',
                    border: 'none',
                    borderBottom: activeTab === 'video' ? '3px solid transparent' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.3s',
                    marginBottom: '-2px'
                  }}
                  onMouseOver={(e) => {
                    if (activeTab !== 'video') {
                      e.target.style.background = '#f7fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeTab !== 'video') {
                      e.target.style.background = 'transparent';
                    }
                  }}
                >
                  🎬 Video
                </button>
              </div>

              {/* Watermarked Image Preview */}
              {activeTab === 'image' && watermarkedImageUrl && (
                <div style={{
                  width: '100%',
                  maxWidth: '600px',
                  margin: '0 auto 30px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  background: '#fff'
                }}>
                  <img
                    src={watermarkedImageUrl}
                    alt="Watermarked Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
              )}

              {/* Video player */}
              {activeTab === 'video' && (
                <div style={{
                  width: '100%',
                  maxWidth: '600px',
                  margin: '0 auto 20px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  <video
                    controls
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                    poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%236b7280'%3EVideo%3C/text%3E%3C/svg%3E"
                  >
                    <source src={videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
            </div>

            {/* Download and Buy Now Section */}
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
              marginBottom: '30px',
              flexWrap: 'wrap'
            }}>
              <button
                className="btn btn-primary"
                onClick={activeTab === 'image' ? handleDownloadImage : handleDownload}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px'
                }}
              >
                {activeTab === 'image' ? '🖼️ Download Image' : '🎬 Download Video'}
              </button>
              {(shopUrl || selectedDesign?.shop_url) && (
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    setShowOutOfStockModal(true);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px'
                  }}
                >
                  <span>🛍️ Buy Now</span>
                  {Object.keys(sizeQuantities).length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      justifyContent: 'center',
                      fontSize: '12px'
                    }}>
                      {Object.entries(sizeQuantities)
                        .sort((a, b) => {
                          const sizeOrder = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, '2XL': 6 };
                          return (sizeOrder[a[0]] || 99) - (sizeOrder[b[0]] || 99);
                        })
                        .map(([size, qty]) => (
                          <span key={size}>
                            {size}:{qty}
                          </span>
                        ))}
                    </div>
                  )}
                </button>
              )}
            </div>

            {/* Share Instantly on Social Media Section */}
            <div style={{
              background: '#f7fafc',
              padding: '30px',
              borderRadius: '12px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                marginBottom: '15px', 
                color: '#1a202c',
                textAlign: 'center',
                fontSize: '20px'
              }}>
                📱 Share Instantly on Social Media
              </h3>
              <p style={{
                textAlign: 'center',
                color: '#718096',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                One-tap sharing with auto-generated captions
              </p>

              {/* Social Media Buttons */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('tiktok') : handleShare('tiktok')}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#000000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  🎵 TikTok
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('instagram') : handleShare('instagram')}
                  style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  📷 Instagram
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('facebook') : handleShare('facebook')}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#1877F2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  👥 Facebook
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('twitter') : handleShare('twitter')}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#1DA1F2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  ✖️ X (Twitter)
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('whatsapp') : handleShare('whatsapp')}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#25D366',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  � WhatsApp
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('copy') : handleShare('copy')}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#6B7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  📋 Copy Link
                </button>
              </div>

              {/* Caption Preview */}
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{ 
                  fontSize: '14px', 
                  marginBottom: '10px',
                  color: '#1a202c',
                  fontWeight: '600'
                }}>
                  Auto-Generated Caption:
                </h4>
                <p style={{ 
                  fontSize: '13px', 
                  color: '#4b5563',
                  whiteSpace: 'pre-line',
                  lineHeight: '1.6'
                }}>
                  {generateCaption()}
                </p>
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={onReset}
              style={{ marginTop: '20px' }}
            >
              ✨ Create Another Video
            </button>
          </div>
        )}
      </div>

      <OutOfStockModal
        isOpen={showOutOfStockModal}
        onClose={() => setShowOutOfStockModal(false)}
      />
    </div>
  );
};

export default GenerateStep;
